import { Fragment } from "react";

import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { ActivityRow } from "../types/paymentsActivity.types";

export interface ActivityStatsStripProps {
  /** The loaded page. The breakdown counts these rows — it is NOT a global aggregate. */
  rows: ActivityRow[];
  /** Server-side count for the active filters. */
  total: number;
  isLoading: boolean;
}

interface Stat {
  key: string;
  label: string;
  value: number;
  /** Whether this number describes the loaded page rather than the whole filtered set. */
  pageScoped: boolean;
  valueClassName?: string;
}

/**
 * Stat strip above the list.
 *
 * Only `total` is authoritative — it is the server's count for the current filters. The runtime has no
 * aggregate-by-status endpoint and the list is paginated, so the breakdown can only describe the rows that
 * are actually loaded. Rather than imply otherwise, those three carry an explicit "on this page" caption; a
 * number that silently meant something narrower than its label would be worse than no number at all.
 */
export function ActivityStatsStrip({ rows, total, isLoading }: ActivityStatsStripProps) {
  const { t } = useTranslations("paymentsActivity");

  const stats: Stat[] = [
    { key: "total", label: t("stats_total"), value: total, pageScoped: false },
    {
      key: "processing",
      label: t("stats_processing"),
      value: rows.filter((row) => row.isNonTerminal).length,
      pageScoped: true,
    },
    {
      key: "settled",
      label: t("stats_settled"),
      value: rows.filter((row) => row.bucket === "settled").length,
      pageScoped: true,
      valueClassName: "text-success",
    },
    {
      key: "failed",
      label: t("stats_failed"),
      value: rows.filter((row) => row.bucket === "failed").length,
      pageScoped: true,
      valueClassName: "text-destructive",
    },
  ];

  return (
    <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm sm:flex sm:flex-wrap sm:items-center sm:gap-6 sm:p-4">
      {/*
       * Below `sm` the four figures are a 2x2 grid whose hairlines are the 1px gaps letting the container's
       * border colour through — so the rules are drawn by the container, which is what structurally prevents
       * the bug this replaced: the separator used to be the first child of each item, so once the row wrapped
       * it travelled to the START of the second line and hung off the left edge.
       *
       * `sm:contents` dissolves this wrapper, putting the cells and the (now `sm:block`) separators back as
       * the direct flex children of the section — the desktop strip keeps the exact layout it had.
       */}
      <div className="grid grid-cols-2 gap-px bg-border sm:contents">
        {stats.map((stat, index) => (
          <Fragment key={stat.key}>
            {index > 0 && <div aria-hidden="true" className="hidden h-8 w-px bg-border sm:block" />}
            <div className="flex flex-col bg-card p-3 sm:block sm:bg-transparent sm:p-0">
              <p className="mb-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {stat.label}
                {stat.pageScoped && (
                  <>
                    {/* The separator belongs to the inline rendering only — on its own line it would read as
                        a bullet. The caption itself never collapses: see this component's doc comment. */}
                    <span aria-hidden="true" className="hidden sm:inline">
                      {" · "}
                    </span>
                    <span className="block normal-case sm:inline">{t("stats_page_scope")}</span>
                  </>
                )}
              </p>
              {/* `mt-auto` bottom-aligns the figures inside the grid row, so the one-line "Total" label
                  cannot leave its number sitting higher than its neighbours'. Inert in the `sm:block` cell. */}
              <p
                className={cn(
                  "mt-auto font-mono text-xl font-bold tabular-nums text-foreground",
                  stat.valueClassName,
                  isLoading && "opacity-40",
                )}
              >
                {stat.value}
              </p>
            </div>
          </Fragment>
        ))}
      </div>
    </section>
  );
}
